import { LoadingSpinner } from "@/components/ui/LoadingSpinner/LoadingSpinner";
import { Button, type ButtonProps } from "@/components/ui/shadcn/Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/DropdownMenu/DropdownMenu";
import { cn } from "@/lib/utils/cn";
import { ChevronDown, type LucideIcon } from "lucide-react";

export interface ActionSplitButtonOption {
  disabled?: boolean;
  icon?: LucideIcon;
  label: string;
  onSelect: () => void;
}

interface ActionSplitButtonProps {
  actions: ActionSplitButtonOption[];
  className?: string;
  disabled?: boolean;
  icon?: LucideIcon;
  label: string;
  loading?: boolean;
  onClick: () => void;
  primaryDisabled?: boolean;
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
}

export const ActionSplitButton = ({
  actions,
  className,
  disabled = false,
  icon: Icon,
  label,
  loading = false,
  onClick,
  primaryDisabled = false,
  size,
  variant = "default",
}: ActionSplitButtonProps) => {
  const isPrimaryDisabled = disabled || primaryDisabled || loading;
  const isMenuDisabled = disabled || loading || actions.length === 0;

  return (
    <div className="flex items-center">
      <Button
        type="button"
        variant={variant}
        size={size}
        className={cn("rounded-r-none", className)}
        disabled={isPrimaryDisabled}
        onClick={onClick}
      >
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {Icon ? <Icon className="h-4 w-4" /> : null}
            {label}
          </>
        )}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant={variant}
            size={size}
            className={cn(
              "rounded-l-none px-2",
              variant === "outline"
                ? "border-l-border -ml-px"
                : "border-l border-l-current/20",
              className,
            )}
            disabled={isMenuDisabled}
            aria-label={`Additional ${label.toLowerCase()} actions`}
          >
            {loading ? <LoadingSpinner /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <DropdownMenuItem
                key={action.label}
                disabled={action.disabled}
                onSelect={action.onSelect}
              >
                {Icon ? <Icon className="mr-2 h-4 w-4" /> : null}
                {action.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
